import type { InboxEventKind } from "./messages";

/**
 * What a person wants to hear about. Approvals and questions always alert (an agent is stuck without you); the rest can be
 * turned down. The same rule decides on the phone (alerts from the open app) and on the computer (alerts that wake the phone).
 */
export interface AlertPrefs {
  /** Tell me when an agent finishes. Default true. */
  finished?: boolean;
  /** Quiet hours: minutes after midnight, in the phone's own time zone (`tz`, an IANA name). `from` > `to` spans midnight. */
  quiet?: { from: number; to: number; tz: string };
}

/** Minutes after midnight in `tz` at `now`; null if the zone is unknown. */
export function minutesIn(tz: string, now: Date): number | null {
  try {
    const parts = new Intl.DateTimeFormat("en-GB", { timeZone: tz, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(now);
    const h = Number(parts.find((p) => p.type === "hour")?.value);
    const m = Number(parts.find((p) => p.type === "minute")?.value);
    return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null;
  } catch {
    return null;
  }
}

export function inQuietHours(quiet: AlertPrefs["quiet"], now: Date): boolean {
  if (!quiet || quiet.from === quiet.to) return false;
  const at = minutesIn(quiet.tz, now);
  if (at === null) return false; // an unknown zone must never silence an approval's cousin: carry on alerting
  return quiet.from < quiet.to ? at >= quiet.from && at < quiet.to : at >= quiet.from || at < quiet.to;
}

export function alertWanted(prefs: AlertPrefs | undefined, kind: InboxEventKind, now: Date = new Date()): boolean {
  if (kind === "approval" || kind === "question") return true;
  if (kind === "completed" && prefs?.finished === false) return false;
  return !inQuietHours(prefs?.quiet, now);
}
